import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook to create a Stripe Checkout session via the create-checkout-session edge function.
 */
export function useCreateCheckout() {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const createCheckout = async (priceId: string) => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      if (!token) {
        toast({ title: 'Error', description: 'Please sign in first.', variant: 'destructive' });
        return null;
      }

      const response = await supabase.functions.invoke('create-checkout-session', {
        body: { price_id: priceId },
      });

      if (response.error) {
        throw new Error(response.error.message || 'Failed to create checkout session');
      }

      const { checkout_url } = response.data;
      if (checkout_url) {
        window.location.href = checkout_url;
        return checkout_url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('[use-create-checkout] Error:', err);
      toast({
        title: 'Checkout Error',
        description: 'Failed to start checkout. Please try again.',
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  };

  return { createCheckout, loading };
}
